import React, { Component } from 'react';
import { observer, PropTypes as MobxPropTypes } from 'mobx-react';
import b from 'b_';
import cn from 'classnames';
import PropTypes from 'prop-types';
import SelectStore from '../../stores/SelectStore';
import { Key } from '../../mirror/userInput';
import './Select.css';

const block = b.lock('SelectContainer');

const DEFAULT_PLACEHOLDER = 'Select...';

export default class Select extends Component {
    static propTypes = {
        options: MobxPropTypes.arrayOrObservableArrayOf(SelectStore.OptionPropType),
        value: SelectStore.OptionValuePropType,
        placeholder: PropTypes.string,
        label: PropTypes.string,
        fullWidth: PropTypes.bool,
        valuesTranslateFunction: PropTypes.func,
        onSelect: PropTypes.func,
        optionsTop: PropTypes.bool,
        containerClassName: PropTypes.string,
    };

    state = {
        focus: false,
        open: false,
        hovered: null,
        selected: this.props.value,
    };

    static defaultProps = {
        options: [],
        placeholder: DEFAULT_PLACEHOLDER,
    };

    constructor(props) {
        super(props);

        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        document.removeEventListener('keydown', this.handleKeyDown);
    }


    componentWillReceiveProps(nextProps) {
        if (nextProps.value !== this.props.value) {
            this.setState({ selected: nextProps.value });
        }
    }

    handleClickOutside = (event) => {
        if (this.selectElement && !this.selectElement.contains(event.target)) {
            if (this.state.open) {
                this.setState({ open: false, hovered: null });
            } else {
                this.setState({ focus: false, hovered: null });
            }
        }
    };

    handleKeyDown = (event) => {
        if (this.state.focus) {
            if (event.keyCode === Key.DOWN || event.keyCode === Key.UP) {
                const isPreviousHovered = this.state.hovered != null;

                if (!isPreviousHovered) {
                    let selectedIndex = this.props.options.findIndex((option) => option.value === this.state.selected);
                    if (selectedIndex < 0) {
                        selectedIndex = this.props.optionsTop ? this.props.options.length - 1 : 0;
                    }
                    this.onHover(selectedIndex);
                }

                if (!this.state.open) {
                    this.setOpen(true);
                } else if (isPreviousHovered || this.state.selected != null) {
                    if (event.keyCode === Key.DOWN && this.state.hovered < this.props.options.length - 1) {
                        this.onHover(this.state.hovered + 1);
                    } else if (event.keyCode === Key.UP && this.state.hovered > 0) {
                        this.onHover(this.state.hovered - 1);
                    }
                }
            } else if (event.keyCode === Key.ENTER && this.state.hovered != null) {
                this.onSelect(this.props.options[this.state.hovered].value);
            }
        }
    };

    onHover = (value) => {
        this.setState({ hovered: value });
    };

    onRef = (element) => {
        this.selectElement = element;
    };

    onSelect = (value) => {
        this.setState({ selected: value, open: false, hovered: null });
        if (this.props.onSelect) {
            this.props.onSelect(value);
        }
    };

    setOpen = (value) => {
        this.setState({ open: value, focus: true });
    };

    getDisplayedValue = (option) => {
        const { valuesTranslateFunction } = this.props;

        if (!option.label) {
            return option.value;
        }

        if (valuesTranslateFunction) {
            return valuesTranslateFunction(option.label);
        }

        return option.label;
    };

    render() {
        const {
            options, placeholder, label, fullWidth,
            disabled, optionsTop, containerClassName,
            ...otherProps
        } = this.props;
        const { focus, open, hovered, selected } = this.state;
        const selectedOption = this.props.options.find((option) => option.value === selected);
        const displayedValue = selectedOption ? this.getDisplayedValue(selectedOption) : placeholder;

        return <div
            ref={this.onRef}
            className={cn(block({ fullWidth }), containerClassName)}
            {...otherProps}
        >
            {label ? <div className={block('Label', { disabled, focus })}>
                {label}
            </div> : null}

            <div className={block('Select', { focus, disabled })} onClick={() => !disabled && this.setOpen(!open)}>
                <div className={block('SelectContent')}>
                    {displayedValue}
                </div>
            </div>

            {open ? <div className={block('OptionList', { optionsTop })}>
                {options.map((option, index) => {
                    const content = this.getDisplayedValue(option);
                    return <div
                        key={option.value}
                        className={block('Option', { hover: index === hovered, select: option.value === selected })}
                        onMouseEnter={() => this.onHover(index)}
                        onMouseLeave={() => this.onHover(null)}
                        onClick={() => this.onSelect(option.value)}
                        title={content}
                    >{content}</div>;
                })}
            </div> : null}
        </div>;
    }

    getValue() {
        return this.state.selected;
    }

    setValue(value) {
        this.setState({ selected: value });
    }

    focus() {
        if (this.selectElement) {
            this.selectElement.focus();
        }
    }

    blur() {
        if (this.selectElement) {
            this.selectElement.blur();
        }
    }
}

export const ObservableSelect = observer(({ store, ...props }) => <Select
    options={store.options}
    value={store.value}
    onSelect={store.setValue}
    ref={store.setRef}
    {...props}
/>);

ObservableSelect.propTypes = {
    store: SelectStore.PropType.isRequired,
    ...Select.propTypes,
};
